import { useEffect, useId, useRef, type KeyboardEvent as ReactKeyboardEvent, type ReactNode } from 'react'
import { useEmployeeTransactions } from './useEmployeeTransactions'
import { trapTab } from '../../lib/focusTrap'
import { formatEmployeeValue, type EmployeePerformanceQueryContext, type EmployeePerformanceRow } from '../../lib/employeePerformance'
import { describeEmployeeTransactionsError, formatTransactionTime } from '../../lib/employeeTransactions'

// ── Employee Performance widget: an employee's receipts ─────────────────
//
// The drill-down. Opened from an employee in the widget, it lists the receipts behind their row for
// exactly the dashboard's current view — the widget's own period and filters, sent as they are, plus
// the employee. Newest first and capped; the totals row always covers every receipt, so it matches
// the widget's row even when the list is cut short, and the dialog says when it is.
//
// Portalled to <body> by the panel: the grid positions each widget with a CSS transform, which would
// trap a position: fixed dialog inside the card. Follows the modal rule — the dimmed backdrop does
// nothing, and the ✕ and Close shut it. It holds no edits, so Escape shuts it as well. Tab stays inside.

export interface EmployeeTransactionsModalProps {
    employee: Pick<EmployeePerformanceRow, 'employeeId' | 'name' | 'departed' | 'manualGrants'>
    /** The widget's own period, filters and page query key. */
    query: EmployeePerformanceQueryContext
    /** The widget's wording for the period: 'Today', 'September 2026', … */
    periodLabel: string
    /** The store selector's wording: 'Calgary', 'All Stores', … */
    storeLabel: string
    onClose: () => void
}

/** react-grid-layout starts a widget drag on these; events from a portal still bubble through React. */
const stopGrid = (e: { stopPropagation: () => void }) => e.stopPropagation()

const money = (n: number) => formatEmployeeValue('currencyCents', n)
const whole = (n: number) => formatEmployeeValue('int', n)
const receipts = (n: number) => `${whole(n)} ${n === 1 ? 'receipt' : 'receipts'}`

const CHANNEL_NAME: Record<string, string> = { instore: 'In-store', online: 'Online' }

export default function EmployeeTransactionsModal({ employee, query, periodLabel, storeLabel, onClose }: EmployeeTransactionsModalProps) {
    const baseId = useId()
    const dialogRef = useRef<HTMLDivElement>(null)
    const { data, error } = useEmployeeTransactions(query, employee.employeeId)
    const { channel, includeReturns, includeMultibuys } = query.filters
    const name = employee.name.trim() || 'Unknown employee'
    const grants = employee.manualGrants ?? 0

    useEffect(() => { dialogRef.current?.focus() }, [])

    const onKeyDown = (e: ReactKeyboardEvent<HTMLDivElement>) => {
        if (e.key === 'Escape') {
            e.stopPropagation()
            onClose()
            return
        }
        trapTab(e, dialogRef.current)
    }

    // The view, spelled out, so it can be checked against the dashboard behind the backdrop.
    const view = [
        periodLabel,
        storeLabel,
        channel === 'instore' ? 'In-Store' : channel === 'online' ? 'Online' : null,
        includeReturns ? null : 'Gross (returns left out)',
        includeMultibuys ? null : 'Multibuys left out',
    ].filter(Boolean).join(' · ')

    let body: ReactNode
    if (error) {
        body = <div className="emp-perf-state" role="status">{describeEmployeeTransactionsError(error)}</div>
    } else if (!data) {
        body = <div className="emp-perf-state" role="status">Loading transactions…</div>
    } else if (data.transactions.length === 0) {
        body = <div className="emp-perf-state" role="status">No receipts for {name} in this view.</div>
    } else {
        const { totals } = data
        body = (
            <>
                {data.truncated && (
                    <p className="emp-txn-note" role="note">
                        Showing the newest {whole(data.transactions.length)} of {receipts(totals.transactions)}. The totals row
                        covers all of them.
                    </p>
                )}
                <div className="emp-txn-scroll" role="region" aria-label={`Receipts rung by ${name}`} tabIndex={0}>
                    <table className="emp-perf-table emp-txn-table">
                        <thead>
                            <tr>
                                <th scope="col">Time</th>
                                <th scope="col">Store</th>
                                <th scope="col">Receipt</th>
                                <th scope="col">Channel</th>
                                <th scope="col" className="emp-perf-num">Units</th>
                                <th scope="col" className="emp-perf-num">{includeReturns ? 'Net Sales' : 'Gross Sales'}</th>
                                <th scope="col" className="emp-perf-num">Discounts</th>
                                <th scope="col" className="emp-perf-num" title="Adventure Bucks credited to the customer on this receipt.">Adv. Bucks</th>
                                <th
                                    scope="col"
                                    title={includeReturns
                                        ? 'The receipt includes a returned item.'
                                        : 'The receipt includes a returned item. With GROSS selected, the return isn’t in these figures.'}
                                >
                                    Return
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {data.transactions.map(t => (
                                <tr key={t.transactionId}>
                                    <th scope="row" className="emp-txn-time">
                                        <time dateTime={t.time} title={t.time.replace('T', ' ')}>
                                            {formatTransactionTime(t.time, data.startDate, data.endDate)}
                                        </time>
                                    </th>
                                    <td>{t.storeName ?? '—'}</td>
                                    <td className="emp-txn-receipt" title={t.till != null ? `Till ${t.till}` : undefined}>
                                        {t.receiptNumber ? `#${t.receiptNumber}` : '—'}
                                    </td>
                                    <td>{CHANNEL_NAME[t.channel] ?? t.channel}</td>
                                    <td className="emp-perf-num">{whole(t.units)}</td>
                                    <td className="emp-perf-num">{money(t.netSales)}</td>
                                    <td className="emp-perf-num">{money(t.discounts)}</td>
                                    <td className="emp-perf-num">{money(t.bucksCredited)}</td>
                                    <td>{t.hasReturn && <span className="emp-perf-tag emp-txn-return">Return</span>}</td>
                                </tr>
                            ))}
                        </tbody>
                        <tfoot>
                            <tr>
                                <th scope="row" colSpan={4}>
                                    <span className="emp-perf-total-label">Total</span>
                                    <span className="emp-perf-sub">
                                        {data.truncated ? `All ${receipts(totals.transactions)}` : receipts(totals.transactions)}
                                    </span>
                                </th>
                                <td className="emp-perf-num">{whole(totals.units)}</td>
                                <td className="emp-perf-num">{money(totals.netSales)}</td>
                                <td className="emp-perf-num">{money(totals.discounts)}</td>
                                <td className="emp-perf-num">{money(totals.bucksCredited)}</td>
                                <td />
                            </tr>
                        </tfoot>
                    </table>
                </div>
            </>
        )
    }

    return (
        <div className="modal-backdrop" onMouseDown={stopGrid} onPointerDown={stopGrid} onTouchStart={stopGrid}>
            <div
                ref={dialogRef}
                className="modal emp-txn"
                role="dialog"
                aria-modal="true"
                aria-label={`Transactions — ${name}`}
                aria-describedby={`${baseId}-view`}
                tabIndex={-1}
                onKeyDown={onKeyDown}
            >
                <div className="modal-header">
                    <div className="emp-txn-heading">
                        <h2>
                            <span>{name}</span>
                            {employee.departed && <span className="emp-perf-tag">former</span>}
                        </h2>
                        <p id={`${baseId}-view`} className="emp-txn-scope">Transactions · {view}</p>
                    </div>
                    <button type="button" className="modal-close" aria-label="Close" onClick={onClose}>✕</button>
                </div>

                <div className="modal-body emp-txn-body">
                    {body}
                    {grants > 0 && (
                        <p className="emp-txn-note">Manual grants ({money(grants)}) aren’t sales, so they aren’t listed here.</p>
                    )}
                </div>

                <div className="modal-footer">
                    <button type="button" className="btn btn-secondary" onClick={onClose}>Close</button>
                </div>
            </div>
        </div>
    )
}
